import { RootState } from 'src/store/store'
import { CameraActiveItemsType, CameraDataPropsType } from 'src/views/Service/Camera'
import { COLOR } from 'src/constants/colors'
import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import useMediaQueryHook from 'src/hooks/useMediaQuery.hook'
import Div from 'src/components/atomic/atom/Div'
import Section from 'src/components/atomic/atom/Section'
import EmptyComponent from 'src/components/atomic/Molecules/EmptyComponent'
import StopWatch from 'src/components/atomic/Molecules/StopWatch'
import Svg from 'src/components/atomic/atom/Svg'
import Span from 'src/components/atomic/atom/Span'
import Button from 'src/components/atomic/atom/Button'
import { useTranslation } from 'react-i18next'
import Lottie from 'lottie-react'
import loadingLottie from 'src/assets/imgs/lottie/mfinder-global-loading-animation.json'
import { useMemo, useState } from 'react'

const CameraTemplate = (props: { data: CameraDataPropsType }) => {
  const { CameraActiveItems, CameraStartHandler } = props.data
  const { t } = useTranslation()
  const { setMediaStyle, currentMedia } = useMediaQueryHook()
  const [blockCamera, setBlockCamera] = useState(false)
  const { EmptyComponentStatus, CameraStatus } = useAppSelector(
    (state: RootState) => state.CameraPage,
  )

  const ContentsComponentStyle = {
    display: 'block',
    position: 'absolute',
    width: '100%',
    top: '50%',
    margin: '0 auto',
    height: CameraStatus !== 'active' ? 'auto' : '100%',
    padding: '0 20px',
    left: currentMedia === 'pc' ? 'unset' : '50%',
    transform: currentMedia === 'pc' ? 'translate(0, -50%)' : 'translate(-50%, -50%)',
  }
  const PublisherStyle = {
    position: 'relative',
    width: '120px',
    height: '200px',
    borderRadius: '8px',
    overflow: 'hidden',
    top: '32px',
    float: 'right',
    right: '28px',
    margin: '0',
    zIndex: 12,
    visibility: CameraStatus === 'active' ? 'visible' : 'hidden ',
  }
  const SubscriberStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    borderRadius: '20px',
    overflow: 'hidden',
  }

  const CameraActive = () => {
    return (
      <Div $position={'relative'} width={'100%'} height={'100%'} $margin={'0 auto'}>
        <Div
          display={'flex'}
          $position={'absolute'}
          $justify_content={'space-between'}
          $margin={'0 auto'}
          $gap={'16px'}
          style={{
            bottom: '40px',
            left: '50%',
            transform: 'translate(-50%,0%)',
          }}
        >
          <StopWatch />
          {CameraActiveItems.map((element: CameraActiveItemsType) => (
            <Section
              key={element.svgName}
              $border_radius={'50px'}
              width={'56px'}
              height={'56px'}
              $bgColor={element.bgColor}
              style={{ pointerEvents: element.disabled ? 'none' : 'pointer' }}
            >
              <Svg
                name={element.svgName}
                onClick={element.onClick}
                color={element.svgColor}
                className={element.className}
              />
            </Section>
          ))}
        </Div>
      </Div>
    )
  }

  const CameraLoading = () => {
    return (
      <>
        <Lottie
          animationData={loadingLottie}
          style={{ margin: '0 auto 14px auto', width: '55px', height: '62px' }}
        />
        <Span color={COLOR.white} option={'extraLarge_bold'}>
          Preparing for Video Call
        </Span>
      </>
    )
  }

  const CameraStart = () => {
    if (!blockCamera) {
      setBlockCamera(true)
      CameraStartHandler()
      // 다중클릭 방지 위해 통화 연결 Delay 감안하여 3초 동안 버튼 비활성화
      setTimeout(() => {
        setBlockCamera(false)
      }, 3000)
    }
  }

  const CameraDefault = () => {
    return (
      <>
        <Span
          display={'flex'}
          option={'tiny'}
          color={COLOR.black30}
          $whitespace={'pre-wrap'}
          width={setMediaStyle('332px', '332px', '280px')}
          $margin={'0 auto'}
        >
          {t('CAMERA.DESC', {
            defaultValue:
              "Video-call your lost device to receive sound and watch device's surroundings - this does not need the handler to take your call.\n\n Tap “Start” to video-call your lost device.",
          })}
        </Span>
        <Button
          $bgColor={COLOR.brand}
          display={'flex'}
          height={'44px'}
          width={'128px'}
          $padding={'0 21px'}
          $align_items={'center'}
          disabled={blockCamera}
          onClick={CameraStart}
          $margin={setMediaStyle('24px auto', '24px auto', '48px auto')}
          className={'camera_connection_start'}
        >
          <Svg name={'video_call_active'} width={'20px'} height={'20px'} />
          <Span
            option={'tiny_bold'}
            color={COLOR.white}
            $margin={'0 10px 0 0'}
            $text_align={'right'}
          >
            {t('BUTTON.CAMERA_START', { defaultValue: 'Start' })}
          </Span>
        </Button>
      </>
    )
  }

  const CameraContents = () => {
    return useMemo(() => {
      switch (true) {
        case EmptyComponentStatus !== 'default':
          return <EmptyComponent option={EmptyComponentStatus} button={true} $margin={'0 auto'} />
        case CameraStatus === 'active':
          return <CameraActive />
        case CameraStatus === 'loading':
          return <CameraLoading />
        default:
          return <CameraDefault />
      }
      // eslint-disable-next-line  react-hooks/exhaustive-deps
    }, [EmptyComponentStatus, CameraStatus])
  }

  return (
    <>
      <Div
        $bgColor={COLOR.black100}
        $margin={setMediaStyle('32px auto 167px auto', '24px 20px', '23px 0 0 0')}
        height={setMediaStyle(
          '880px',
          `calc(var(--vh, 1vh) * 100 - 160px)`,
          `calc(var(--vh, 1vh) * 100 - 136px)`,
        )}
        $border_radius={'20px'}
        $position={'relative'}
      >
        <Div id="subscriber" style={SubscriberStyle} />
        <Div id="publisher" style={PublisherStyle} />
        <Section style={ContentsComponentStyle}>
          <CameraContents />
        </Section>
      </Div>
    </>
  )
}
export default CameraTemplate
